import React from 'react';
import { CompleteKundali, Language } from '../../types/kundali';
import { LifeThemeId } from '../../types/advancedLifeAnalysis';
import { analyzeCrossVargaConvergence } from '../../utils/v518/vargaConvergenceEngine';
import { 
  Layers, 
  Sparkles, 
  CheckCircle2, 
  AlertTriangle, 
  ShieldCheck, 
  ChevronRight,
  Compass
} from 'lucide-react';

interface VargaConvergenceCardProps {
  kundali: CompleteKundali;
  themeId?: LifeThemeId;
  lang?: Language;
}

export const VargaConvergenceCard: React.FC<VargaConvergenceCardProps> = ({
  kundali,
  themeId = 'career_job',
  lang = 'en'
}) => {
  const isHi = lang === 'hi';
  const convergence = analyzeCrossVargaConvergence(kundali, themeId, lang);

  const getConvergenceBadge = (level: typeof convergence.convergenceLevel) => {
    switch (level) {
      case 'VERY_HIGH':
      case 'HIGH':
        return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30';
      case 'MODERATE':
        return 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30';
      default:
        return 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30';
    }
  };

  return (
    <div id="v518-varga-convergence-card" className="bg-white dark:bg-astro-primary border border-astro-border dark:border-astro-strong-border rounded-xl p-5 md:p-6 shadow-sm">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-astro-border dark:border-astro-strong-border">
        <div className="flex items-center gap-2.5">
          <span className="p-2 rounded-lg bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
            <Layers className="w-5 h-5" />
          </span>
          <div>
            <h3 className="text-lg font-semibold text-astro-text dark:text-white flex items-center gap-2">
              {isHi ? 'बहु-वर्गीय अभिसरण एवं पुष्टि' : 'Cross-Varga Convergence Matrix'}
              <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 font-normal">
                V5.16
              </span>
            </h3>
            <p className="text-xs text-astro-muted dark:text-astro-muted">
              {isHi ? convergence.themeNameHi : convergence.themeNameEn} • {convergence.applicableVargas.join(', ')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto">
          <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${getConvergenceBadge(convergence.convergenceLevel)}`}>
            {convergence.convergenceLevel} ({convergence.convergenceIndex}%)
          </span>
        </div>
      </div>

      {/* Varga Signals Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 my-5">
        {convergence.signals.map((sig) => (
          <div
            key={sig.vargaCode}
            id={`varga-signal-${sig.vargaCode}`}
            className="p-3.5 rounded-lg bg-astro-secondary-surface dark:bg-astro-secondary/50 border border-astro-border/70 dark:border-astro-strong-border/60 flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center justify-between font-semibold text-xs text-astro-text dark:text-white mb-1">
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                  {sig.vargaCode}
                </span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                  sig.signalStrength === 'STRONG_POSITIVE' || sig.signalStrength === 'POSITIVE'
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                    : sig.signalStrength === 'CHALLENGED'
                    ? 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300'
                    : 'bg-astro-card text-astro-secondary-text dark:bg-astro-secondary dark:text-astro-muted'
                }`}>
                  {sig.signalStrength.replace('_', ' ')}
                </span>
              </div>
              <div className="text-[11px] text-astro-muted dark:text-astro-muted mb-1.5">
                {sig.houseSign} ({sig.houseLord}) • {isHi ? 'भाव: ' : 'House: '}{sig.relevantHouse}
              </div>
              <p className="text-xs text-astro-secondary-text dark:text-astro-muted leading-relaxed">
                {isHi ? sig.rationaleHi : sig.rationaleEn}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Synthesis Verdict */}
      <div className="p-3.5 rounded-lg bg-astro-secondary-surface dark:bg-astro-secondary/40 border border-astro-border/80 dark:border-astro-strong-border/60 text-xs text-astro-secondary-text dark:text-astro-muted">
        <span className="font-semibold text-astro-text dark:text-white">
          {isHi ? 'समन्वय निष्कर्ष: ' : 'Convergence Verdict: '}
        </span>
        {isHi ? convergence.synthesisVerdictHi : convergence.synthesisVerdictEn}
      </div>
    </div>
  );
};
